import React, { useCallback, useEffect } from 'react';
import cx from 'classnames';

import {
  BetManager,
  Bus,
  constants,
  getConfigProp,
  globalBetslipStateUpdate,
  isEmptyCollection,
  isUserActionNeedPhotoVerification,
  Logger,
  needsVerification,
  NotificationManager,
  PhotoVerificationAction,
  useApplicationState,
  useBetslipState,
  useBetslipStateUpdate,
} from '@atlas/core';
import { BetslipErrors, Button, Icon, Label, MoneyInput } from '@atlas/ui';

import { validateSelectedBet } from 'common/Betslip/BetslipClassic';
import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import useBonuses from 'common/Betslip/useBonuses';
import useSelectedBonuses from 'common/Betslip/useSelectedBonuses';

import AcceptOddsChangesSelect from './BetsMoneyBlock/AcceptOddsChangesSelect';
import BetslipSignInButton from './BetsMoneyBlock/BetslipSignInButton';
import GetMaxBetButton from './BetsMoneyBlock/GetMaxBetButton';
import InfoSection from './BetsMoneyBlock/InfoSection';
import PlaceBetButton from './BetsMoneyBlock/PlaceBetButton';
import MultipleControls from './MultipleControls';

const betAmountMaxValue = 9223e13;
const emptySelectedBet = {};

const DefaultControls = ({
  currency,
  isShowPreOrderBtn,
  placeBetDisabled,
  selectedBetTypeIndex,
  setSelectedBetTypeIndex,
  vip,
}) => {
  const betslipStateUpdate = useBetslipStateUpdate();
  const { setIsOpenPopup } = useLocalBetslip();
  const isSportRadarProject = getConfigProp('isSportRadarProject');

  const { amount, betItems, betTypes, bonuses, loading } = useBetslipState();

  const { account, authenticated, bankAccounts } = useApplicationState();

  const [active, setActive] = React.useState(false);
  const [placeBetCall, togglePlaceBetCall] = React.useState(false);
  const betslipLoading = loading || placeBetCall;

  const isSingleBet = betItems && betItems.length === 1;

  // Selected bet type or single bet item
  const selectedBet = betTypes[selectedBetTypeIndex] || betItems[0] || emptySelectedBet;

  const { multipleBonuses } = useBonuses(isSportRadarProject ? [] : bonuses, selectedBet);

  const isBetItemsActive = React.useMemo(
    () => betItems.every(bet => bet.active && !bet.suspended),
    [betItems],
  );

  const hasInactiveBets = React.useMemo(
    () => betItems.some(bet => !bet.active || bet.suspended),
    [betItems],
  );

  const goToVerificationPage = useCallback(() => {
    setIsOpenPopup(false);
    return NotificationManager.display({
      message: 'verificationPopup',
      type: 'verificationPopup',
    });
  }, [setIsOpenPopup]);

  const { removeSelectedBonuses, selectBonus, selectedBonuses, selectedFreebet } =
    useSelectedBonuses();

  const applicationState = useApplicationState();
  const {
    wallet: {
      detailedBalance: { BONUS: balanceBonus, CASH: balanceCash, FROZEN: balanceFROZEN = 0 } = {},
    } = {},
  } = applicationState;

  const balance = Math.max(parseInt(balanceCash), parseInt(balanceBonus));
  const overallBalance = balanceCash + balanceBonus + balanceFROZEN;

  // Clean up reports if user adding bet item to betslip
  useEffect(() => {
    if (betItems.length > 0) {
      globalBetslipStateUpdate({ reports: [] });
    }

    // Removing selected bonuses when betslip changes
    removeSelectedBonuses();
  }, [betItems.length, removeSelectedBonuses]);

  // Auto reset side effect for selected bet type index
  React.useEffect(() => {
    if (!isEmptyCollection(betTypes) && betTypes.length - 1 < selectedBetTypeIndex) {
      setSelectedBetTypeIndex(betTypes.length - 1);
    }
  }, [betTypes, selectedBetTypeIndex]);

  // Cleaning selected bonuses when selected bet changes
  React.useEffect(() => {
    removeSelectedBonuses();

    if (selectedBet.requestId) {
      // Resetting validation status
      betslipStateUpdate({
        validationStatus: constants.BETSLIP_VALIDATION_STATUS.WAITING_VALIDATION,
      });
    } else {
      // Canceling validation on unselect
      validateSelectedBet.cancel();
    }
  }, [selectedBet.requestId]);

  const betAmountChangeHandle = useCallback(
    amount => {
      if (selectedFreebet) {
        return;
      }

      if (amount > betAmountMaxValue) {
        Logger.info('bet amount is too big');
        return;
      }

      betslipStateUpdate({ amount });
    },
    [betslipStateUpdate, selectedFreebet],
  );

  const handlePlaceBet = useCallback(
    params => {
      const bet = {
        ...selectedBet,
        ...params,
        amount,
      };

      // Adding selected bonuses
      if (!isEmptyCollection(selectedBonuses)) {
        // Setting bonuses
        bet.bonusInfoList = selectedBonuses;

        if (selectedFreebet) {
          bet.amount = selectedFreebet.freeBetAmount;
        }
      }

      // Placing selected bet from main betslip
      BetManager.placeBets([bet], { id: 'main', vip });
    },
    [selectedBet, selectedBonuses, selectedFreebet, amount, vip],
  );

  const preHandlePlaceBet = React.useCallback(() => {
    if (hasInactiveBets) {
      const activeBetItems = betItems.filter(bet => {
        if (!bet.active || bet.suspended) {
          BetManager.removeBet(bet);
          return;
        }

        return bet;
      });

      if (activeBetItems.length && amount) {
        togglePlaceBetCall(true);
      }
    } else {
      handlePlaceBet();
    }
  }, [hasInactiveBets, betItems, amount, handlePlaceBet]);

  React.useEffect(() => {
    const validationSuccessHandler = response => {
      togglePlaceBetCall(false);
      const { odds, outcomes, requestId } = response.bets[0];

      handlePlaceBet({
        odds,
        outcomes,
        requestId,
      });
      Bus.off(Bus.events.bet.validationSuccess, validationSuccessHandler);
    };

    const validationErrorHandler = () => {
      togglePlaceBetCall(false);
      Bus.off(Bus.events.bet.validationError, validationErrorHandler);
    };

    if (placeBetCall) {
      Bus.on(Bus.events.bet.validationSuccess, validationSuccessHandler);
      Bus.on(Bus.events.bet.validationError, validationErrorHandler);
    }

    return () => {
      Bus.off(Bus.events.bet.validationSuccess, validationSuccessHandler);
      Bus.off(Bus.events.bet.validationError, validationErrorHandler);
    };
  }, [placeBetCall]);

  const hasSelectedFreeBet = !isEmptyCollection(selectedFreebet);

  const inputClassname = cx(hasSelectedFreeBet && 'has-value');
  const bottomButtonContainerCls = cx(
    'bottom-button-container',
    !isShowPreOrderBtn && 'bottom-button-container_with_padding',
  );

  const renderBetslipButton = () => {
    if (!authenticated) {
      return <BetslipSignInButton />;
    }

    const showVerificationButton =
      needsVerification(account, bankAccounts) &&
      isUserActionNeedPhotoVerification(account, PhotoVerificationAction.BET_SLIP);

    if (showVerificationButton) {
      return <Button label="verify_account" onClick={goToVerificationPage} />;
    }

    return (
      <PlaceBetButton
        balance={balance}
        betAmount={selectedFreebet?.freeBetAmount || amount}
        currency={currency}
        disabled={placeBetDisabled}
        hasInactiveBets={hasInactiveBets}
        loading={betslipLoading}
        onSubmit={preHandlePlaceBet}
        selectedFreebet={selectedFreebet}
        vip={vip}
      />
    );
  };

  return (
    <div className="betslip-classic-controls">
      <BetslipErrors />

      {authenticated && overallBalance < amount && !selectedFreebet && (
        <div className="betslip-bet-report__header">
          <Icon className="betslip-bet-report__error" name="warning" />
          <Label className="betslip-bet-report__title" message="betslip_error_insufficient_funds" />
        </div>
      )}

      {!isSingleBet && (
        <MultipleControls
          selectedBet={selectedBet}
          selectedBetTypeIndex={selectedBetTypeIndex}
          setSelectedBetTypeIndex={setSelectedBetTypeIndex}
        />
      )}

      <div className="input-max-button-container">
        <div className="input-max-button">
          <MoneyInput
            active={active}
            className={inputClassname}
            currency={currency}
            dataTestId="betslip-amount"
            disabled={betslipLoading || hasSelectedFreeBet}
            key={selectedBet.outcomeId || selectedBet.requestId}
            label="Amount"
            onBlur={() => setActive(false)}
            onChange={betAmountChangeHandle}
            onFocus={() => setActive(true)}
            value={selectedFreebet?.freeBetAmount || amount}
          />
          {authenticated && isBetItemsActive && (
            <GetMaxBetButton
              betItems={betItems}
              disabled={hasSelectedFreeBet}
              loadingBtnMax={betslipLoading}
              selectedBet={selectedBet}
              setBetAmount={betAmountChangeHandle}
            />
          )}
        </div>
      </div>

      <InfoSection
        betAmount={selectedFreebet?.freeBetAmount || amount}
        currency={currency}
        isSingleBet={isSingleBet}
        multipleBonuses={multipleBonuses}
        removeSelectedBonuses={removeSelectedBonuses}
        selectBonus={selectBonus}
        selectedBet={selectedBet}
        selectedFreebet={selectedFreebet}
      />

      <div className={bottomButtonContainerCls}>
        {!isSportRadarProject && <AcceptOddsChangesSelect />}

        {renderBetslipButton()}
      </div>
    </div>
  );
};

export default DefaultControls;
